// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SwiperClass from "swiper/types/swiper-class";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import RecentlyPurchased, {
    HeaderRecentlyPurchasedWidgetId,
    RecentlyPurchasedStyles,
} from "@keller/App/Components/Header/RecentlyPurchased";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import { MenuWrapper } from "@keller/MobiusOverrides/Menu";
import { MappedLink } from "@keller/Overrides/Widgets/Header/MainNavigation";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import FloatingTooltip, { FloatingTooltipStyles } from "@keller/App/Components/FloatingTooltip/FloatingTooltip";

interface RecentlyPurchasedLinkProps {
    menuLink: React.ReactElement;
    isOpen: boolean;
    link: MappedLink;
}

const mapStateToProps = (state: CustomAppState) => ({
    canViewPurchasedProducts: xIsAuthenticated(state),
    products: getProductsForProductInfoList(state, HeaderRecentlyPurchasedWidgetId),
});

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = RecentlyPurchasedLinkProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface RecentlyPurchasedLinkStyles {
    floatingTooltip?: FloatingTooltipStyles;
    recentlyPurchasedStyle?: RecentlyPurchasedStyles;
}

export const recentlyPurchasedLinkStyles: RecentlyPurchasedLinkStyles = {
    recentlyPurchasedStyle: {
        carouselProductStyles: {
            productGridLayoutStyle: {
                image: {
                    css: css`
                        @media screen and (max-height: 900px) {
                            height: 130px;
                        }
                    `,
                },
            },
            productImageStyles: {
                linkWrappingImage: {
                    css: css`
                        @media screen and (max-height: 900px) {
                            width: 130px;
                            height: 130px;
                        }
                    `,
                },
            },
        },
    },
};

const styles = recentlyPurchasedLinkStyles;

const RecentlyPurchasedLink: FC<Props> = ({ menuLink, products, link, isOpen, loadPurchasedProducts }) => {
    const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);

    useEffect(() => {
        if (!products || products.length === 0) {
            loadPurchasedProducts({ widgetId: HeaderRecentlyPurchasedWidgetId, purchaseType: "recentlyFromHeader" });
        }
    }, []);

    const updateSwiperInstance = (swiperInstance: SwiperClass | null) => {
        setSwiperInstance(swiperInstance);
    };

    return (
        <FloatingTooltip
            onOpenChange={open => {
                if (open) {
                    swiperInstance?.updateSlides();
                    swiperInstance?.update();
                }
            }}
            triggerComponent={
                <MenuWrapper>
                    {React.cloneElement(menuLink as React.ReactElement<React.HTMLAttributes<HTMLElement>>, {
                        id: "RecentlyPurchasedLink",
                        className: `trigger trigger-item`,
                    })}
                </MenuWrapper>
            }
            trigger="hover"
            placement="bottom-end"
            delay={{ open: 350, close: 500 }}
            showArrow={false}
            overlay={true}
            offset={0}
            extendedStyles={styles.floatingTooltip}
        >
            <RecentlyPurchased
                link={link}
                setSwiperInstance={updateSwiperInstance}
                extendedStyles={styles.recentlyPurchasedStyle}
            />
        </FloatingTooltip>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RecentlyPurchasedLink);
